import React from 'react';
import { createPortal } from 'react-dom';

import { SEED_VARIETY_FLYOUT_CLOSE_LABEL } from 'constants/uiConstants';
import { GcVariety } from 'interfaces/IGlobalCatalogue';

import FlyoutHeader from './FlyoutHeader';
import useFlyoutPosition from './useFlyoutPosition';
import VarietyListPanel from './VarietyListPanel';

interface SeedVarietyFlyoutProps {
  anchorRef: React.RefObject<HTMLElement>;
  cropLabel: string;
  varieties: GcVariety[];
  highlightedVariety: GcVariety | null;
  searchText: string;
  decideLaterLabel: string;
  onSearchChange: (value: string) => void;
  onSelectVariety: (variety: GcVariety, event?: React.MouseEvent) => void;
  onDecideLater: (event?: React.MouseEvent) => void;
  onClose: () => void;
}

/**
 * Seed variety list portaled beside the step picker, placed from `anchorRef`.
 * Kept as a plain portal rather than a nested antd Popover since OP-2323's
 * click-outside crash (@rc-component/trigger 3.10.1 now null-guards it).
 * Controlled by StepInputSelector so useStepSelectorKeyboard can drive it.
 */
const SeedVarietyFlyout: React.FC<SeedVarietyFlyoutProps> = ({
  anchorRef,
  cropLabel,
  varieties,
  highlightedVariety,
  searchText,
  decideLaterLabel,
  onSearchChange,
  onSelectVariety,
  onDecideLater,
  onClose,
}) => {
  const { position, portalContainer } = useFlyoutPosition(anchorRef);

  if (!position || !portalContainer) return null;

  return createPortal(
    <div
      style={
        {
          '--flyout-top': `${position.top}rem`,
          '--flyout-left': `${position.left}rem`,
          '--flyout-width': `${position.width * 0.5}rem`,
          '--flyout-height': `${position.height}rem`,
        } as React.CSSProperties
      }
      tabIndex={-1}
      className="step-picker-flyout z-[1080] flex flex-col outline-none overflow-hidden rounded-l-none rounded-lg border-l bg-white"
    >
      <FlyoutHeader
        title={cropLabel}
        closeLabel={SEED_VARIETY_FLYOUT_CLOSE_LABEL}
        onClose={onClose}
      />
      <VarietyListPanel
        className="min-h-0"
        varieties={varieties}
        searchText={searchText}
        highlightedVariety={highlightedVariety}
        decideLaterLabel={decideLaterLabel}
        onSearchChange={onSearchChange}
        onSelectVariety={onSelectVariety}
        onDecideLater={onDecideLater}
      />
    </div>,
    portalContainer
  );
};

export default SeedVarietyFlyout;
